import { formatInTimeZone, toZonedTime } from 'date-fns-tz';

const toDate = (value: Date | string): Date =>
  value instanceof Date ? value : new Date(value);

/**
 * Standard timezone abbreviation mapping
 * Maps IANA timezone identifiers to their standard abbreviations
 * This ensures consistent labels (e.g., IST instead of GMT+5:30)
 */
const TIMEZONE_ABBREVIATION_MAP: Record<string, string> = {
  'Asia/Kolkata': 'IST',
  'America/Toronto': 'EST', // Will be EDT during daylight saving
  'America/Los_Angeles': 'PST', // Will be PDT during daylight saving
  'America/Chicago': 'CST', // Will be CDT during daylight saving
  'America/Denver': 'MST', // Will be MDT during daylight saving
  'America/New_York': 'EST', // Will be EDT during daylight saving
  'America/Vancouver': 'PST', // Will be PDT during daylight saving
};

/**
 * Get timezone abbreviation for a given date and timezone
 * @param date - The date to format (in UTC)
 * @param timeZone - IANA timezone (e.g., 'America/Toronto', 'America/Los_Angeles', 'Asia/Kolkata')
 * @returns Timezone abbreviation (e.g., 'EST', 'EDT', 'PST', 'PDT', 'IST')
 */
export const getTimeZoneAbbreviation = (date: Date, timeZone?: string): string => {
  const tz = timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  
  // For IST (Asia/Kolkata), always return 'IST' since it doesn't have DST
  // and Intl API returns "GMT+5:30" instead of "IST"
  if (tz === 'Asia/Kolkata') {
    return 'IST';
  }
  
  // For other timezones with standard mappings, use Intl API to get DST-aware abbreviation
  // This ensures we get EST/EDT, PST/PDT correctly based on the date
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    timeZoneName: 'short',
  });
  
  const parts = formatter.formatToParts(date);
  const tzName = parts.find((part) => part.type === 'timeZoneName')?.value;
  
  // If Intl returns a GMT offset (e.g., "GMT+5:30", "GMT+530"), use our mapping as fallback
  if (tzName && tzName.startsWith('GMT')) {
    return TIMEZONE_ABBREVIATION_MAP[tz] || tzName;
  }
  
  // Return the abbreviation from Intl API (works well for EST/EDT, PST/PDT, etc.)
  return tzName || TIMEZONE_ABBREVIATION_MAP[tz] || '';
};

/**
 * Format time in appointment taker's timezone
 * @param value - Date or date string (in UTC)
 * @param pattern - Date format pattern (default: 'h:mm a')
 * @param timeZone - Appointment taker's timezone (defaults to browser timezone)
 * @returns Formatted time string with timezone abbreviation
 */
export const formatTimeWithZone = (
  value: Date | string,
  pattern = 'h:mm a',
  timeZone?: string
): string => {
  const date = toDate(value);
  const tz = timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  
  // Format the time in the appointment taker's timezone
  const formattedTime = formatInTimeZone(date, tz, pattern);
  const abbreviation = getTimeZoneAbbreviation(date, tz);
  const result = abbreviation ? `${formattedTime} ${abbreviation}` : formattedTime;
  
  return result;
};

/**
 * Format date and time in appointment taker's timezone
 * @param value - Date or date string (in UTC)
 * @param datePattern - Date format pattern (default: 'EEEE, MMMM d, yyyy')
 * @param timePattern - Time format pattern (default: 'h:mm a')
 * @param timeZone - Appointment taker's timezone (defaults to browser timezone)
 * @returns Formatted date and time string with timezone abbreviation
 */
export const formatDateTimeWithZone = (
  value: Date | string,
  datePattern = 'EEEE, MMMM d, yyyy',
  timePattern = 'h:mm a',
  timeZone?: string
): string => {
  const date = toDate(value);
  const tz = timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const formattedDate = formatInTimeZone(date, tz, datePattern);
  const formattedTime = formatTimeWithZone(date, timePattern, tz);

  return `${formattedDate} • ${formattedTime}`;
};

/**
 * Convert UTC date to appointment taker's timezone
 * @param utcDate - Date in UTC
 * @param timeZone - Appointment taker's timezone (defaults to browser timezone)
 * @returns Date object in appointment taker's timezone
 */
export const convertToTimeZone = (
  utcDate: Date | string,
  timeZone?: string
): Date => {
  const date = toDate(utcDate);
  const tz = timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  return toZonedTime(date, tz);
};

/**
 * Format date only (no time) in appointment taker's timezone
 * @param value - Date or date string (in UTC)
 * @param pattern - Date format pattern (default: 'MMMM d, yyyy')
 * @param timeZone - Appointment taker's timezone (defaults to browser timezone)
 * @returns Formatted date string
 */
export const formatDateWithZone = (
  value: Date | string,
  pattern = 'MMMM d, yyyy',
  timeZone?: string
): string => {
  const date = toDate(value);
  const tz = timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  return formatInTimeZone(date, tz, pattern);
};

/**
 * Get time in minutes from a UTC date in a specific timezone
 * @param utcDate - Date in UTC
 * @param timeZone - IANA timezone (e.g., 'America/Toronto', 'America/Los_Angeles')
 * @returns Time in minutes (0-1439)
 */
export const getTimeInMinutesInZone = (utcDate: Date, timeZone: string): number => {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });
  
  const parts = formatter.formatToParts(utcDate);
  const hour = parseInt(parts.find(p => p.type === 'hour')!.value);
  const minute = parseInt(parts.find(p => p.type === 'minute')!.value);
  return hour * 60 + minute;
};

